import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const TOKEN_KEY = 'agwh_admin_token';

export const auth = {
  getToken: () => (typeof window === 'undefined' ? null : window.localStorage.getItem(TOKEN_KEY)),
  setToken: (t) => window.localStorage.setItem(TOKEN_KEY, t),
  clear: () => window.localStorage.removeItem(TOKEN_KEY),
  isAuthed: () => Boolean(typeof window !== 'undefined' && window.localStorage.getItem(TOKEN_KEY)),
};

export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const t = auth.getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && typeof window !== 'undefined') {
      auth.clear();
      if (!window.location.pathname.startsWith('/admin/login')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(err);
  }
);

export const publicApi = {
  siteConfig: () => api.get('/site-config').then((r) => r.data),
  featuredVideo: () => api.get('/featured-video').then((r) => r.data),
  featuredVideoThumbnail: () => api.get('/featured-video/thumbnail').then((r) => r.data),
  seasonList: (params = {}) => api.get('/season-entries', { params }).then((r) => r.data),
  seasonDetail: (slug) => api.get(`/season-entries/${slug}`).then((r) => r.data),
  fieldNotes: (params = {}) => api.get('/field-notes', { params }).then((r) => r.data),
  fieldNoteDetail: (slug) => api.get(`/field-notes/${slug}`).then((r) => r.data),
  subscribe: (payload) => api.post('/newsletter/subscribe', payload).then((r) => r.data),
  contact: (payload) => api.post('/contact', payload).then((r) => r.data),
  recaps: () => api.get('/recaps').then((r) => r.data),
  recapDetail: (yearMonth) => api.get(`/recaps/${yearMonth}`).then((r) => r.data),
};

export const adminApi = {
  login: (payload) => api.post('/auth/login', payload).then((r) => r.data),
  me: () => api.get('/auth/me').then((r) => r.data),
  stats: () => api.get('/admin/stats').then((r) => r.data),

  listSeason: () => api.get('/admin/season-entries').then((r) => r.data),
  getSeason: (id) => api.get(`/admin/season-entries/${id}`).then((r) => r.data),
  createSeason: (payload) => api.post('/admin/season-entries', payload).then((r) => r.data),
  updateSeason: (id, payload) => api.put(`/admin/season-entries/${id}`, payload).then((r) => r.data),
  deleteSeason: (id) => api.delete(`/admin/season-entries/${id}`).then((r) => r.data),
  duplicateSeason: (id) => api.post(`/admin/season-entries/${id}/duplicate`).then((r) => r.data),

  listNotes: () => api.get('/admin/field-notes').then((r) => r.data),
  getNote: (id) => api.get(`/admin/field-notes/${id}`).then((r) => r.data),
  createNote: (payload) => api.post('/admin/field-notes', payload).then((r) => r.data),
  updateNote: (id, payload) => api.put(`/admin/field-notes/${id}`, payload).then((r) => r.data),
  deleteNote: (id) => api.delete(`/admin/field-notes/${id}`).then((r) => r.data),
  duplicateNote: (id) => api.post(`/admin/field-notes/${id}/duplicate`).then((r) => r.data),

  updateVideo: (payload) => api.put('/admin/featured-video', payload).then((r) => r.data),
  updateConfig: (payload) => api.put('/admin/site-config', payload).then((r) => r.data),

  subscribers: () => api.get('/admin/subscribers').then((r) => r.data),
  deleteSubscriber: (id) => api.delete(`/admin/subscribers/${id}`).then((r) => r.data),

  messages: () => api.get('/admin/contact-messages').then((r) => r.data),
  markMessageRead: (id, read = true) =>
    api.patch(`/admin/contact-messages/${id}`, null, { params: { read } }).then((r) => r.data),
  deleteMessage: (id) => api.delete(`/admin/contact-messages/${id}`).then((r) => r.data),

  listRevisions: (entryType, entryId) =>
    api.get(`/admin/revisions/${entryType}/${entryId}`).then((r) => r.data),
  getRevision: (revisionId) =>
    api.get(`/admin/revisions/one/${revisionId}`).then((r) => r.data),
  restoreRevision: (revisionId) =>
    api.post(`/admin/revisions/one/${revisionId}/restore`).then((r) => r.data),

  listRecaps: () => api.get('/admin/recaps').then((r) => r.data),
  getRecap: (yearMonth) => api.get(`/admin/recaps/${yearMonth}`).then((r) => r.data),
  updateRecap: (yearMonth, payload) =>
    api.put(`/admin/recaps/${yearMonth}`, payload).then((r) => r.data),
  recapEmail: (yearMonth) => api.get(`/admin/recaps/${yearMonth}/email`).then((r) => r.data),
  broadcastRecap: (yearMonth, force = false) =>
    api.post(`/admin/recaps/${yearMonth}/broadcast`, { force }).then((r) => r.data),

  emailStatus: () => api.get('/admin/email-status').then((r) => r.data),
  sendTestEmail: (payload = {}) =>
    api.post('/admin/send-test-email', payload).then((r) => r.data),

  resetContent: (payload = {}) => api.post('/admin/reset-content', payload).then((r) => r.data),
};
